import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { Context } from '@/components/Context'
import { ArtData } from '@/database/art'
import Button from '@/components/Button'
import { uni1d800 } from '@/utils/extras'

const MAX_SALE_VALUE = '666'
const MAXIMUM_EXPIRATION = '168'
const MINIMUM_EXPIRATION = '12'

const Price = ({ artData }: { artData: ArtData }): ReactElement => {
    const { accountSignature, saleHandler, loading, setLoading } = useContext(Context)
    const [saleValue, setSaleValue] = useState('')
    const [expirationRadio, setExpirationRadio] = useState('none')
    const [expiration, setExpiration] = useState('')

    const getSaleValue = () => (saleValue === '' ? '-1' : saleValue)

    const getButtonName = () => {
        if (saleValue === '') {
            return 'Not For Sale'
        }
        return expirationRadio === 'none' ? `Set For Sale` : `Set For Auction`
    }

    function onChangeRadio(event: { target: { value: React.SetStateAction<string> } }) {
        setExpirationRadio(event.target.value)
        setExpiration(event.target.value === 'none' ? '' : MINIMUM_EXPIRATION.toString())
    }

    const handleExpirationInputChange = (event: { target: { value: string } }) => {
        setExpirationRadio('none')
        const input = event.target.value.replace(',', '.') // Normalize input

        // Check if the last character is a dot or if the input is just a dot
        const endsWithDot = input.endsWith('.') || input === '.'

        // Use a regular expression to validate if the input is in a correct numeric format (including dots)
        const isValidNumericInput = /^(\d+\.?\d*|\.\d+)$/.test(input)

        if (endsWithDot || isValidNumericInput) {
            // Directly update the state with the input if it's a valid numeric input or ends with a dot
            const newInput = parseFloat(input)
            if (newInput > parseFloat(MAXIMUM_EXPIRATION)) {
                setExpiration(MAXIMUM_EXPIRATION)
            } else {
                setExpiration(input)
            }
            setExpirationRadio('12')
        } else if (input === '') {
            setExpiration('')
        } else {
            setExpiration('12')
            setExpirationRadio('12')
        }
    }

    const handleBidInputChange = (event: { target: { value: string } }) => {
        const input = event.target.value.replace(',', '.') // Normalize input

        // Check if the last character is a dot or if the input is just a dot
        const endsWithDot = input.endsWith('.') || input === '.'

        // Use a regular expression to validate if the input is in a correct numeric format (including dots)
        const isValidNumericInput = /^(\d+\.?\d*|\.\d+)$/.test(input)

        if (endsWithDot || isValidNumericInput) {
            // Directly update the state with the input if it's a valid numeric input or ends with a dot
            const newInput = parseFloat(input)
            if (newInput > parseFloat(MAX_SALE_VALUE)) {
                setSaleValue(MAX_SALE_VALUE.toString())
            } else {
                setSaleValue(input)
            }
        } else if (input === '') {
            setSaleValue('')
        } else {
            setSaleValue(`0`)
        }
    }

    const validateExpiration = () => {
        const parsedExpiration = parseFloat(expiration)
        if (expirationRadio != 'none' && (parsedExpiration < 12 || isNaN(parsedExpiration))) {
            setExpiration(MINIMUM_EXPIRATION)
            setExpirationRadio('12')
        } else if (parsedExpiration > parseFloat(MINIMUM_EXPIRATION)) {
            setExpiration(MAXIMUM_EXPIRATION)
            setExpirationRadio('12')
        }
        return true
    }

    useEffect(() => {
        setLoading(false)
    }, [])

    return (
        <div className="bid-expiration-container artwork-box">
            <div className="bid-container">
                <span className={uni1d800.className + ' gray'}>{'\uD836\uDE17CRAZY\u200C\uD836\uDE17'}</span>
                <input
                    type="range"
                    min={expirationRadio === 'none' ? artData.art.transferFee : '0'}
                    max={MAX_SALE_VALUE}
                    step="0.01"
                    value={parseFloat(saleValue) || 0}
                    onChange={(event) => setSaleValue(parseFloat(event.target.value).toFixed(2))}
                />
                <input type="text" min="0" max={MAX_SALE_VALUE} value={saleValue} onChange={handleBidInputChange} />
            </div>
            <div className="bid-container">
                <div className="gray">Auction Expiration</div>
                <div className="expiration-section">
                    <input
                        id="none"
                        type="radio"
                        value="none"
                        name="expiration"
                        checked={expirationRadio === 'none'}
                        onChange={onChangeRadio}
                    />
                    <label htmlFor="none">none</label>
                    <input
                        id="12h"
                        type="radio"
                        value="12"
                        name="expiration"
                        checked={expirationRadio === '12'}
                        onChange={onChangeRadio}
                    />
                    <label htmlFor="12h">12h</label>
                </div>
                <div>
                    <input
                        type="text"
                        min="12"
                        max={MAXIMUM_EXPIRATION}
                        value={expiration}
                        onChange={handleExpirationInputChange}
                    />
                    <div className="gray">(12 - 168 hours)</div>
                </div>
                <Button
                    btnName={getButtonName()}
                    classStyles={'nav__medium large-button'}
                    disabled={
                        !accountSignature ||
                        loading ||
                        (Number(getSaleValue()) < artData.art.transferFee && expirationRadio === 'none')
                    }
                    handleClick={() =>
                        accountSignature
                            ? validateExpiration() && saleHandler(artData, getSaleValue(), parseFloat(expiration))
                            : alert('Please connect your wallet to proceed transaction')
                    }
                    cancelBg={undefined}
                />
            </div>
        </div>
    )
}
export default Price
